/**
 * Passage highlighter — shared external store.
 *
 * WHY THIS EXISTS: PassagePanel (desktop) and PassageCard (narrow) are hidden from each other
 * with CSS (`hidden lg:flex` / `landscape:hidden lg:hidden`), NOT unmounted. Both are in the
 * DOM on every viewport, so both run the highlighter hook at once. Two useState-backed hook
 * instances would hold divergent range lists and race each other on the localStorage write.
 *
 * A module-level store consumed through useSyncExternalStore gives every consumer one
 * snapshot and one writer, at the cost of a single `sessionId` prop threaded to each host.
 *
 * All localStorage access is wrapped: Safari private mode throws on setItem, and quota can be
 * exhausted. Failure degrades to in-memory highlights for the session — the passage stays
 * readable either way, so there is nothing worth surfacing to a child mid-comprehension.
 */

import {
  MAX_STORED_SESSIONS,
  STORAGE_KEY,
  clampRanges,
  parseStore,
  pruneToRecent,
  type HighlightColor,
  type HighlightRange,
  type HighlightStoreData,
} from '@/lib/passageHighlights';

export interface HighlightSnapshot {
  ranges: HighlightRange[];
  armedColor: HighlightColor | null;
}

/**
 * Stable identity, returned for every inert (sessionId-less) consumer. useSyncExternalStore
 * compares snapshots by reference and would loop forever on a fresh object each call.
 */
const EMPTY_SNAPSHOT: HighlightSnapshot = Object.freeze({ ranges: Object.freeze([]) as unknown as HighlightRange[], armedColor: null });

const listeners = new Map<string, Set<() => void>>();
const snapshots = new Map<string, HighlightSnapshot>();
const hydratedSessions = new Set<string>();

function readAll(): HighlightStoreData {
  try {
    return parseStore(localStorage.getItem(STORAGE_KEY));
  } catch {
    return {};
  }
}

function writeAll(data: HighlightStoreData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Private mode / quota exceeded. Highlights stay in memory for this session.
  }
}

function notify(sessionId: string): void {
  listeners.get(sessionId)?.forEach((listener) => listener());
}

function setSnapshot(sessionId: string, next: HighlightSnapshot): void {
  snapshots.set(sessionId, next);
  notify(sessionId);
}

export function subscribe(sessionId: string, listener: () => void): () => void {
  if (!sessionId) return () => {};

  let set = listeners.get(sessionId);
  if (!set) {
    set = new Set();
    listeners.set(sessionId, set);
  }
  set.add(listener);

  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(sessionId);
  };
}

export function getSnapshot(sessionId: string): HighlightSnapshot {
  if (!sessionId) return EMPTY_SNAPSHOT;
  return snapshots.get(sessionId) ?? EMPTY_SNAPSHOT;
}

/**
 * Load persisted ranges for this session. Idempotent — both mounted hosts call it.
 *
 * No-ops while the passage is still empty (React Query loading), because clamping against
 * length 0 would discard every stored range and the next write would persist the loss.
 */
export function hydrate(sessionId: string, passageLength: number): void {
  if (!sessionId || passageLength === 0 || hydratedSessions.has(sessionId)) return;
  hydratedSessions.add(sessionId);

  const stored = readAll()[sessionId];
  const ranges = clampRanges(stored?.ranges ?? [], passageLength);
  if (ranges.length === 0) return; // Leave EMPTY_SNAPSHOT in place; nothing to render.

  setSnapshot(sessionId, { ranges, armedColor: getSnapshot(sessionId).armedColor });
}

export function setRanges(sessionId: string, ranges: HighlightRange[], passageLength: number): void {
  if (!sessionId) return;

  const clamped = clampRanges(ranges, passageLength);
  setSnapshot(sessionId, { ranges: clamped, armedColor: getSnapshot(sessionId).armedColor });

  const data = readAll();
  if (clamped.length === 0) {
    delete data[sessionId];
  } else {
    data[sessionId] = { ranges: clamped, updatedAt: Date.now() };
  }
  writeAll(pruneToRecent(data, MAX_STORED_SESSIONS));
}

/** Which pen is armed. Deliberately NOT persisted — every session starts with the pen down. */
export function setArmedColor(sessionId: string, armedColor: HighlightColor | null): void {
  if (!sessionId) return;
  setSnapshot(sessionId, { ranges: getSnapshot(sessionId).ranges, armedColor });
}

/**
 * Module state outlives a Vitest file's localStorage.clear() in tests/setup.ts, so tests must
 * reset it explicitly between cases.
 */
export function __resetForTests(): void {
  listeners.clear();
  snapshots.clear();
  hydratedSessions.clear();
}
